// src/components/events/EventDetail/index.tsx
// Event 詳情主組件 - 使用 Local-first 樂觀更新和 Inline Editing

import React from 'react';
import { ArrowLeft } from '../../../../components/common/icons';
import ResponsiveModal from '../../../../components/common/ResponsiveModal';
import { useToast } from '../../../../contexts/ToastContext';
import { useConfirmDialog } from '../../../../contexts/ConfirmDialogContext';
import { useEventDetail } from '../../hooks/useEventDetail';
import { EventDetailContent } from './EventDetailContent';
import { EventDetailMetadata } from './EventDetailMetadata';
import { EventDetailActions } from './EventDetailActions';

// ============================================
// Props 接口
// ============================================

interface EventDetailProps {
  isOpen: boolean;
  eventId: string | null;
  onClose: () => void;
  onRefresh?: () => void;
}

// ============================================
// 主組件
// ============================================

const EventDetail: React.FC<EventDetailProps> = ({
  isOpen,
  eventId,
  onClose,
  onRefresh,
}) => {
  const { showSuccess, showError } = useToast();
  const { confirmDelete } = useConfirmDialog();

  // 使用自定義 Hook
  const {
    event,
    isLoading,
    error,
    isSubmitting,
    updateTitle,
    updateNotes,
    updateCategory,
    updateStartTime,
    updateEndTime,
    updateTimeType,
    updateLocation,
    deleteEvent,
  } = useEventDetail({
    eventId,
    isOpen,
    onClose,
    onRefresh,
    showSuccess,
    showError,
  });

  // ============================================
  // 事件處理
  // ============================================

  const handleDelete = async () => {
    if (!event) return;

    const confirmed = await confirmDelete(event.title || 'this event');

    if (confirmed) {
      await deleteEvent();
    }
  };

  // ============================================
  // 渲染
  // ============================================

  // Loading 狀態
  if (isLoading && !event) {
    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        leftAction={
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <ArrowLeft size={20} />
          </button>
        }
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </ResponsiveModal>
    );
  }

  // Error 狀態
  if (error || !event) {
    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        leftAction={
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <ArrowLeft size={20} />
          </button>
        }
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">{error || 'Event not found'}</div>
        </div>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Event Details"
      leftAction={
        <button
          className="text-gray-500 hover:text-gray-700"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <ArrowLeft size={20} />
        </button>
      }
      saveButton={undefined}
    >
      <div className="flex flex-col h-full">
        {/* Main content area - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
          <div className="flex flex-col md:flex-row md:gap-6 min-h-full">
            {/* Content Area */}
            <EventDetailContent
              event={event}
              onUpdateTitle={updateTitle}
              onUpdateNotes={updateNotes}
              onUpdateCategory={updateCategory}
            />

            {/* Metadata Sidebar (Desktop) */}
            <EventDetailMetadata
              event={event}
              onStartTimeChange={updateStartTime}
              onEndTimeChange={updateEndTime}
              onTimeTypeChange={updateTimeType}
              onLocationChange={updateLocation}
              variant="desktop"
            />

            {/* Metadata Section (Mobile) */}
            <EventDetailMetadata
              event={event}
              onStartTimeChange={updateStartTime}
              onEndTimeChange={updateEndTime}
              onTimeTypeChange={updateTimeType}
              onLocationChange={updateLocation}
              variant="mobile"
            />
          </div>
        </div>

        {/* Bottom action area */}
        <div className="shrink-0 border-t border-gray-100 px-4 py-3 sm:py-4 bg-white">
          <EventDetailActions
            onDelete={handleDelete}
            isSubmitting={isSubmitting}
          />
          <div className="h-safe-bottom" />
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default EventDetail;
